/**
 * The backend wraps every response — success and failure alike — in the same
 * three-field object (see `app/schemas/response.py` and the error handlers in
 * `app/main.py`). This module is the single place that shape is decoded, so the
 * rest of the portal deals in plain values.
 */

/**
 * All three fields are optional because that is how the backend's generated
 * schema describes them: Pydantic marks them with defaults, so the OpenAPI
 * document does not list them as required. `unwrap` handles every combination.
 */
export interface Envelope<T> {
  message?: string | null;
  data?: T | null;
  error?: string | null;
}

/** Text the backend returns when a session has been signed out elsewhere. */
const REVOKED_SESSION_TEXT = 'Session has been revoked or expired';

/**
 * What to say when the backend gave us nothing to quote.
 *
 * Status 0 means the request never got an answer at all - the network dropped,
 * the server is not running, a proxy refused it. These messages are shown to
 * people, and "HTTP 0" told them nothing they could act on.
 */
function describeStatus(status: number): string {
  if (status === 0) {
    return 'We could not reach the server. Check your connection and try again.';
  }
  if (status >= 500) {
    return 'Something went wrong at our end. Please try again.';
  }
  return 'That request could not be completed. Please try again.';
}

/** One field the server refused, in the server's own words. */
export interface ServerFieldError {
  /** The server's name for the field. Empty for a whole-record message. */
  field: string;
  message: string;
}

/**
 * The structured list a failed envelope may carry as `data.errors`.
 *
 * Nothing sends it yet - it is the format proposed to the backend - but
 * reading it now means the portal needs no change the day it lands, and the
 * sentence parser in serverErrors.ts becomes a fallback.
 *
 * Exported for the client's error interceptor, which builds the ApiError
 * itself from the same envelope shape.
 */
export function fieldErrorsFrom(data: unknown): ServerFieldError[] | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const errors = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return undefined;
  const entries = errors.filter(
    (entry): entry is ServerFieldError =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as { field?: unknown }).field === 'string' &&
      typeof (entry as { message?: unknown }).message === 'string',
  );
  return entries.length > 0 ? entries : undefined;
}

/** A failed API call, carrying the HTTP status alongside the backend's message. */
export class ApiError extends Error {
  readonly status: number;
  readonly apiError: string | null;
  readonly envelopeMessage: string | null;
  /** Field-by-field refusals, when the failed envelope carried `data.errors`. */
  readonly fieldErrors: ServerFieldError[] | undefined;

  constructor(
    status: number,
    apiError: string | null,
    envelopeMessage: string | null,
    fieldErrors?: ServerFieldError[],
  ) {
    super(apiError ?? envelopeMessage ?? describeStatus(status));
    this.name = 'ApiError';
    this.status = status;
    this.apiError = apiError;
    this.envelopeMessage = envelopeMessage;
    this.fieldErrors = fieldErrors;
  }

  /**
   * True when the session was deliberately ended (signed out on another device,
   * or revoked by staff). Refreshing the token cannot fix this, so the caller
   * must sign the user out instead of retrying.
   *
   * Matched with `includes` rather than an exact comparison because the backend
   * appends "Please log in again." to the message.
   */
  get isRevokedSession(): boolean {
    return this.status === 401 && (this.apiError ?? '').includes(REVOKED_SESSION_TEXT);
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * What the backend actually said, or null when it said nothing.
   *
   * `message` always has something in it, because a person has to be shown
   * something - but that something may be this module's generic wording. A
   * screen that has better wording of its own needs to know which it got, or it
   * shows "that request could not be completed" where it could have said "that
   * code is not valid or has expired".
   */
  get serverMessage(): string | null {
    return this.apiError ?? this.envelopeMessage;
  }

  /** 4xx. Retrying these never helps — the request itself is the problem. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/** True when a response body looks like the backend's envelope. */
export function isEnvelope(value: unknown): value is Envelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    'error' in value &&
    'message' in value
  );
}

/**
 * Returns the payload from an envelope, or throws an {@link ApiError}.
 *
 * A 200 response carrying no data is treated as a fault rather than an empty
 * result: the backend uses an empty list or object for "nothing here", so a null
 * `data` means something went wrong that was not reported in `error`.
 */
export function unwrap<T>(envelope: Envelope<T> | undefined, status = 200): T {
  if (!envelope) {
    throw new ApiError(status, null, 'The server returned an empty response body.');
  }
  if (envelope.error) {
    throw new ApiError(
      status,
      envelope.error,
      envelope.message ?? null,
      // A failed envelope's data is usually null; when the backend adopts the
      // structured error list, this is where it arrives.
      fieldErrorsFrom(envelope.data),
    );
  }
  if (envelope.data === null || envelope.data === undefined) {
    throw new ApiError(
      status,
      null,
      envelope.message ?? 'The server response contained no data.',
    );
  }
  return envelope.data;
}
